import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { ReactComponent as PlusBlack } from "../../assets/images/plus-black.svg";
import { ReactComponent as MinusBlack } from "../../assets/images/minus-black.svg";
import { ReactComponent as DropdownIcon } from "../../assets/images/dropdown.svg";
import { ReactComponent as ClearIcon } from "../../assets/images/clear-icon.svg";
import CTAPopup from "./CTAPopup";
import { formatNumbers, formatNumbers0 } from "./../../utils/formatNumbers";
import { parseTierIncrement } from "../../utils/ticketTier";
import { isTableTier, stepForTier } from "../../utils/table";
import { ReactComponent as CloseIcon } from "../../assets/images/cross.svg";
import { ReactComponent as ErrorIcon } from "../../assets/images/input-error.svg";

import Footer from "../../components/Utils/Footer";
import PreRegisterForm from "../Event/PreregistrationForm";

import EventSummary from "./EventSummary";
import Select from "react-select";

import SlotSelector from "./SlotSelector";
import TableRequestPrompt from "../TableRequests/TableRequestPrompt";
import { EVENTS, track } from "../../utils/tracking";

/** Matches $ctapopup-height in src/styles/_base.scss (fixed .cta-popup bar) */
const CTA_POPUP_HEIGHT_PX = 70;
/** Minimum space (px) we want between the bottom of the seat counter and the top of the Find Passes control */
const MIN_CLEARANCE_TO_FIND_PASSES_PX = 70;

/**
 * Closest ancestor that both allows overflow scrolling and actually has scrollable overflow.
 * (e.g. .event-page often has overflow:auto but grows with content, so scrollBy is a no-op;
 * in that case the window/document scrolls instead.)
 */
function findScrollableAncestor(el) {
  if (!el) return null;
  let node = el.parentElement;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const { overflowY, overflow } = style;
    const canScrollY =
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll)/.test(overflow);
    if (canScrollY && node.scrollHeight > node.clientHeight + 2) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function scrollWindowBy(delta) {
  if (delta <= 0) return;
  window.scrollBy({ top: delta, behavior: "auto" });
}

const formatSlotTimeCompact = (timeValue) => {
  if (!timeValue) return "";

  if (typeof timeValue === "string" && (timeValue.includes("T") || timeValue.includes(" "))) {
    const parsed = new Date(timeValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed
        .toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
        .replace(/\s/g, "")
        .toLowerCase();
    }
  }

  if (typeof timeValue === "string" && timeValue.includes(":")) {
    const [h, m] = timeValue.split(":");
    const hour = parseInt(h, 10);
    if (!Number.isNaN(hour) && m != null) {
      const ampm = hour >= 12 ? "pm" : "am";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${m.slice(0, 2)}${ampm}`;
    }
  }

  return "";
};

const formatSlotSelectionSummary = (slot) => {
  if (!slot) return "";

  const rawStartTime =
    slot.start_time || slot.startTime || slot.time || slot.startsAt || slot.date;
  const rawEndTime = slot.end_time || slot.endTime || slot.endsAt;
  const rawDate = slot.date || slot.startDate || slot.startsAt || rawStartTime;

  const parsedDate = rawDate ? new Date(rawDate) : null;
  const dateLabel =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "";
  const startTimeLabel = formatSlotTimeCompact(rawStartTime);
  const endTimeLabel = formatSlotTimeCompact(rawEndTime);
  const timeRangeLabel =
    startTimeLabel && endTimeLabel
      ? `${startTimeLabel} – ${endTimeLabel}`
      : startTimeLabel;

  if (dateLabel && timeRangeLabel) return `${dateLabel} · ${timeRangeLabel}`;
  if (dateLabel) return dateLabel;
  if (timeRangeLabel) return timeRangeLabel;

  return slot.name || slot.label || "Selected slot";
};

/**
 * Top edge (viewport Y) of the Find Passes area: prefer the primary button, else the whole CTA strip.
 * Falls back to layout estimate if the bar is not in the DOM yet.
 */
function getFindPassesReferenceTopPx() {
  const visibleBar = document.querySelector(
    ".cta-popup:not(.cta-popup--hidden):not(.cta-popup--disabled)"
  );
  if (visibleBar) {
    const btn = visibleBar.querySelector(".cta-popup__btn");
    const target = btn || visibleBar;
    const style = window.getComputedStyle(target);
    if (style.display !== "none" && style.visibility !== "hidden") {
      return target.getBoundingClientRect().top;
    }
  }
  const viewportH = window.visualViewport?.height ?? window.innerHeight;
  return viewportH - CTA_POPUP_HEIGHT_PX;
}

/**
 * For each seat counter: measure distance from counter bottom to Find Passes (px).
 * If distance < MIN_CLEARANCE_TO_FIND_PASSES_PX, scroll by (minClearance - distance)
 * so the gap becomes at least that many pixels.
 */
function scrollTicketCountersClearOfCta(bodyEl) {
  if (!bodyEl) return;
  const counters = bodyEl.querySelectorAll(".event-page__ticket-counter");
  if (!counters.length) return;

  const findPassesTop = getFindPassesReferenceTopPx();
  let maxScrollNeeded = 0;

  counters.forEach((el) => {
    const counterRect = el.getBoundingClientRect();
    const counterBottom = counterRect.bottom;
    // Positive = counter sits above the button; negative = overlap
    const distanceToButtonPx = findPassesTop - counterBottom;
    if (distanceToButtonPx < MIN_CLEARANCE_TO_FIND_PASSES_PX) {
      const scrollThisMuch =
        MIN_CLEARANCE_TO_FIND_PASSES_PX - distanceToButtonPx;
      if (scrollThisMuch > maxScrollNeeded) maxScrollNeeded = scrollThisMuch;
    }
  });

  if (maxScrollNeeded <= 0) return;

  const scrollable = findScrollableAncestor(counters[0]);
  if (scrollable) {
    scrollable.scrollBy({ top: maxScrollNeeded, behavior: "auto" });
    return;
  }
  scrollWindowBy(maxScrollNeeded);
}

/** Parent plus any sub-tiers that advertise `hasSlots`; used to merge slot calendars. */
function buildSlotTicketsWithSubs(parentTicket, allTickets) {
  const list = [];
  const pushIfSlots = (t, isSubTier = false) => {
    if (t?.hasSlots) list.push({ pk: t.id, tier: t, isSubTier });
  };
  pushIfSlots(parentTicket, false);
  for (const subId of parentTicket?.subTiers ?? []) {
    const sub = allTickets.find((x) => x.id === subId);
    pushIfSlots(sub, true);
  }
  return list;
}

const TicketingDetails = ({
  tickets = [],
  setTickets = () => { },
  highLevelCategories = [],
  sethighLevelCategories = () => { },
  layoutImage = null,
  selectedTickets = {},
  setSelectedTickets = () => { },
  handleTicketClick = () => { },
  handleTicketsFetch = () => { },
  loading = false,
  ticketCount = 0,
  setTicketCount = () => { },
  fetchError = "",
  setFetchError = () => { },
  expiry = null,
  cart = {},
  nextStep = () => { },
  handleRetry = () => { },
  event = {},
  selectedCurrency = {},
  handleCurrencyChange = () => { },
  close = () => { },
  selectedSlots = {},
  setSelectedSlots = () => { },
  onSlotConfirmed = () => { },
}) => {
  const [btnState, setBtnState] = useState({ state: "new", label: "fetch" });
  const [expandedTickets, setExpandedTickets] = useState({});
  const [priceMapper, setPriceMapper] = useState([]);
  const [preregistrationTicketId, setPreregistrationTicketId] = useState(null);
  const [slotModalTicketId, setSlotModalTicketId] = useState(null);
  const [pendingSlotSelection, setPendingSlotSelection] = useState(null);
  const [pendingPersonCount, setPendingPersonCount] = useState(1);
  const personsCounterRef = useRef(null);
  const parseTicketLimit = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.floor(parsed);
  };
  const maxTicketsPerCart =
    parseTicketLimit(event?.maxTicketsPerCart) ??
    parseTicketLimit(event?.max_tickets_per_cart) ??
    4;

  const chartRef = useRef(null);
  const bodyRef = useRef(null);

  const selectedSlotsKey = JSON.stringify(selectedSlots ?? {});
  const expandedTicketsKey = JSON.stringify(expandedTickets);
  const selectedTicketsKey = JSON.stringify(selectedTickets);

  // useLayoutEffect(() => {
  //   try {
  //     const parsed = JSON.parse(selectedTicketsKey || "{}");
  //     if (Object.keys(parsed).length === 0) return;
  //   } catch {
  //     return;
  //   }
  //   if (loading) return;
  //   // Double rAF: measure after layout + paint so rects and scrollHeight are final
  //   let id2;
  //   const id1 = requestAnimationFrame(() => {
  //     id2 = requestAnimationFrame(() => {
  //       scrollTicketCountersClearOfCta(bodyRef.current);
  //     });
  //   });
  //   return () => {
  //     cancelAnimationFrame(id1);
  //     if (id2 != null) cancelAnimationFrame(id2);
  //   };
  // }, [loading, selectedSlotsKey, expandedTicketsKey, selectedTicketsKey]);

  useEffect(() => {
    getCount();
    if (Object.keys(cart).length !== 0) {
      let add = false;
      let remove = false;
      let same = true;
      if (Object.keys(cart).length === Object.keys(selectedTickets).length) {
        for (const ticket in selectedTickets) {
          if (cart[ticket]) {
            if (cart[ticket].quantity > selectedTickets[ticket].count) {
              remove = true;
              same = false;
            } else if (cart[ticket].quantity < selectedTickets[ticket].count) {
              add = true;
              same = false;
            }
          } else {
            add = true;
            same = false;
          }
        }
      } else {
        same = false;
        if (Object.keys(cart).length > Object.keys(selectedTickets).length) {
          remove = true;
          for (const ticket in cart) {
            if (
              selectedTickets[ticket] &&
              selectedTickets[ticket].count > cart[ticket].quantity
            ) {
              add = true;
            }
          }
        } else {
          add = true;
          for (const ticket in selectedTickets) {
            if (
              cart[ticket] &&
              cart[ticket].quantity < selectedTickets[ticket].count
            ) {
              remove = true;
            }
          }
        }
      }
      if (same) {
        setBtnState({ state: "nochange", label: "checkout" });
      } else {
        if (remove && !add) {
          setBtnState({ state: "remove", label: "checkout" });
        } else if (add && !remove) {
          setBtnState({ state: "add", label: "fetch" });
        } else if (add && remove) {
          setBtnState({ state: "change", label: "fetch" });
        }
      }
    }
  }, [selectedTickets]);

  useEffect(() => {
    if (event?.isSeated) {
      handleTicketsMapping();
    }
  }, [tickets]);

  useEffect(() => {
    if (
      tickets?.length !== 0 &&
      !event.isSeated &&
      !loading &&
      window?.innerWidth <= 768
    ) {
      const firstTicketDiv = document.getElementsByClassName(
        "event-page__ticket-area"
      )[0];
      const container = document.getElementsByClassName("event-page__body")[0];
      if (firstTicketDiv && container) {
        setTimeout(() => {
          container.scrollTo({
            top: firstTicketDiv?.getBoundingClientRect()?.top || 0,
            behavior: "smooth",
          });
        }, 1000);
      }
    }
  }, [tickets, loading, event]);

  tickets.filter((t) => !t.isSubTier).forEach((ticket) => {
    let cat = highLevelCategories.find((cat) => cat.id === ticket.id);
    if (!cat) {
      cat = {
        ...ticket.category,
      };
      if (!highLevelCategories.find((cat) => cat.pk === ticket.category?.pk)) {
        highLevelCategories.push(cat);
      }
    }
  });

  const getCount = () => {
    if (Object.keys(selectedTickets).length === 0) {
      setTicketCount(0);
    } else {
      let sumValues = 0;
      for (const tier in selectedTickets) {
        sumValues += selectedTickets[tier].count;
      }
      setTicketCount(sumValues);
    }
  };

  const handleTicketsMapping = () => {
    let priceArray = [];
    tickets.forEach((ticket) => {
      priceArray.push({
        category: ticket?.name,
        price: ticket?.price,
      });
    });
    setPriceMapper(priceArray);
  };

  const handleChartTicketChange = async (choice, type) => {
    let index = tickets.findIndex(
      (ticket) =>
        ticket.name.toLowerCase() === choice.category.label.toLowerCase()
    );
    if (index >= 0) {
      handleTicketClick(
        tickets[index].id,
        tickets[index].name,
        tickets[index].price,
        type,
        choice.label
      );
    }
  };

  const handleChartLoaded = async (chart) => {
    chartRef.current = chart;
    let categories = await chartRef.current.listCategories();
    let ticketsCopy = [...tickets];
    ticketsCopy.forEach((ticket) => {
      let index = categories.findIndex(
        (category) => category.label.toLowerCase() === ticket.name.toLowerCase()
      );
      if (index >= 0) {
        ticket.color = categories[index].color;
      }
    });
    setTickets(ticketsCopy);
  };

  const handleClearTickets = () => {
    track(EVENTS.REMOVED_TICKET, { clearedAll: true });
    setSelectedTickets({});
    if (event?.isSeated) {
      chartRef.current.clearSelection();
    }
  };

  const handleDeselectSeat = (seat) => {
    chartRef.current.deselectObjects([seat]);
  };

  const getSlotAdjustedPrice = (ticket, slot) =>
    slot?.price != null ? slot.price / 100 : ticket.price;

  const getSlotFreeSeats = (slot) => {
    if (!slot) return null;
    const parseSeatValue = (value) => {
      if (value == null) return null;
      if (typeof value === "string" && value.trim() === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
    };
    const possibleValues = [
      slot.availableCapacity,
      slot.available_capacity,
      slot.freeSeats,
      slot.free_seats,
      slot.remainingSeats,
      slot.remaining_seats,
    ];
    for (const value of possibleValues) {
      const parsed = parseSeatValue(value);
      if (parsed != null) {
        return parsed;
      }
    }
    return null;
  };

  const getSlotMaxSeatsToBook = (slot) => {
    if (!slot) return null;
    const parseSeatValue = (value) => {
      if (value == null) return null;
      if (typeof value === "string" && value.trim() === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
    };
    const possibleValues = [
      slot.maxSeatsToBook,
      slot.maxSeatToBook,
      slot.max_seats_to_book,
      slot.max_seat_to_book,
      slot.maxBooking,
      slot.max_booking,
    ];
    for (const value of possibleValues) {
      const parsed = parseSeatValue(value);
      if (parsed != null) {
        return parsed;
      }
    }
    return null;
  };

  const getEffectiveSlotSeatLimit = (slot) => {
    const slotFreeSeats = getSlotFreeSeats(slot);
    const slotMaxSeatsToBook = getSlotMaxSeatsToBook(slot);

    if (slotFreeSeats != null && slotMaxSeatsToBook != null) {
      return Math.min(slotFreeSeats, slotMaxSeatsToBook);
    }
    if (slotFreeSeats != null) {
      return slotFreeSeats;
    }
    if (slotMaxSeatsToBook != null) {
      return slotMaxSeatsToBook;
    }
    return null;
  };

  const formatSlotTime = (timeValue) => {
    if (!timeValue) return "";
    if (typeof timeValue === "string" && (timeValue.includes("T") || timeValue.includes(" "))) {
      const parsedTime = new Date(timeValue);
      if (!Number.isNaN(parsedTime.getTime())) {
        return parsedTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
    }
    if (typeof timeValue === "string" && timeValue.includes(":")) {
      const [h, m] = timeValue.split(":");
      const hour = parseInt(h, 10);
      if (!Number.isNaN(hour) && m != null) {
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${hour12}:${m.slice(0, 2)} ${ampm}`;
      }
    }
    return "";
  };

  const formatSelectedSlotDate = (slot) => {
    if (!slot) return "";

    const rawDate = slot.date || slot.startsAt || slot.startDate || slot.startTime || slot.start_time;
    const parsedDate = rawDate ? new Date(rawDate) : null;
    const dateLabel =
      parsedDate && !Number.isNaN(parsedDate.getTime())
        ? parsedDate.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
        : "";

    const rawStartTime = slot.start_time || slot.startTime || slot.time || slot.date;
    const timeLabel = formatSlotTime(rawStartTime);

    if (dateLabel && timeLabel) return `${dateLabel}, ${timeLabel}`;
    if (dateLabel) return dateLabel;
    if (timeLabel) return timeLabel;

    return "Slot selected";
  };

  const openSlotModal = (ticket) => {
    setSlotModalTicketId(ticket.id);
    const existingSlot = selectedSlots[ticket.id] || null;
    setPendingSlotSelection(existingSlot);
    const tierIncrement = parseTierIncrement(ticket.tierIncrement);
    const currentCount = selectedTickets[ticket.id]?.count || 0;
    setPendingPersonCount(currentCount >= tierIncrement ? currentCount : tierIncrement);
  };

  const closeSlotModal = () => {
    setSlotModalTicketId(null);
    setPendingSlotSelection(null);
  };

  const applySlotSelection = (ticketId, slot, shouldCloseModal = true) => {
    if (!ticketId || !slot) return;

    const selectedTicket = tickets.find((ticket) => ticket.id === ticketId);
    if (!selectedTicket) {
      if (shouldCloseModal) {
        closeSlotModal();
      }
      return;
    }

    const slotPrice = getSlotAdjustedPrice(selectedTicket, slot);
    const slotSeatLimit = getEffectiveSlotSeatLimit(slot);

    setSelectedSlots((prev) => ({
      ...prev,
      [ticketId]: slot,
    }));

    setSelectedTickets((prev) => {
      const key = ticketId;
      const prevEntry = prev[key];
      const prevCount = prevEntry?.count ?? 0;

      if (event?.isSeated) {
        if (!prevEntry) return prev;
        const capped =
          slotSeatLimit != null
            ? Math.min(prevCount, slotSeatLimit)
            : prevCount;
        return {
          ...prev,
          [key]: { ...prevEntry, count: capped, price: slotPrice },
        };
      }

      const otherTierHasTickets =
        !event?.allowMultipleTiers &&
        Object.keys(prev).some(
          (id) => String(id) !== String(key) && (prev[id]?.count ?? 0) > 0
        );

      if (prevCount >= 1) {
        const capped =
          slotSeatLimit != null ? Math.min(prevCount, slotSeatLimit) : prevCount;
        return {
          ...prev,
          [key]: { ...prevEntry, count: capped, price: slotPrice },
        };
      }

      if (otherTierHasTickets) return prev;

      const cartOthers = Object.keys(prev).reduce((sum, id) => {
        if (String(id) === String(key)) return sum;
        return sum + (prev[id]?.count ?? 0);
      }, 0);
      const room = Math.max(0, maxTicketsPerCart - cartOthers);
      const tierIncrement = stepForTier(
        selectedTicket,
        parseTierIncrement(selectedTicket.tierIncrement)
      );

      let nextCount = Math.min(tierIncrement, room);
      if (nextCount < tierIncrement) return prev;
      if (slotSeatLimit != null) {
        nextCount = Math.min(nextCount, slotSeatLimit);
      }
      if (nextCount < tierIncrement) return prev;

      return {
        ...prev,
        [key]: {
          name: selectedTicket.name,
          currency: selectedTicket.currency,
          count: nextCount,
          price: slotPrice,
        },
      };
    });

    if (shouldCloseModal) {
      closeSlotModal();
    }
  };

  const applySlotSelectionWithCount = (ticketId, slot, count) => {
    if (!ticketId || !slot || count == null) return;
    const selectedTicket = tickets.find((t) => t.id === ticketId);
    if (!selectedTicket) return;
    const slotPrice = getSlotAdjustedPrice(selectedTicket, slot);
    const slotSeatLimit = getEffectiveSlotSeatLimit(slot);
    const cappedCount = slotSeatLimit != null ? Math.min(count, slotSeatLimit) : count;
    setSelectedSlots((prev) => ({ ...prev, [ticketId]: slot }));
    setSelectedTickets((prev) => ({
      ...prev,
      [ticketId]: {
        name: selectedTicket.name,
        currency: selectedTicket.currency,
        count: cappedCount,
        price: slotPrice,
      },
    }));
  };

  return (
    <React.Fragment>
      <div ref={bodyRef} className="event-page__body">
        <div className="event-page__description event-page__description--padded">
          <EventSummary event={event} />
          {event?.id && event?.hasTables ? (
            <TableRequestPrompt
              eventId={event.id}
              prompt="Looking for a table?"
              action="Request one"
            />
          ) : null}
          <div className="event-page__ticket-subheader">

            <div className="event-page__ticket-subheader-left">
              <p className="event-page__ticket-max-warning">
                You are allowed to purchase a maximum of {maxTicketsPerCart} Passes
              </p>
              {!event?.allowMultipleTiers && (
                <p className="event-page__ticket-max-warning">
                  You can only select one type of pass in your basket
                </p>
              )}

            </div>

            <div className="event-page__ticket-subheader-right">
              <div className="event-page__region-select--container">
                <label htmlFor="region-select">
                  <h2 className="event-page__region-select--label">
                    Region
                    {event?.regions?.length < 2
                      ? `: ${event?.regions?.[0]?.label}`
                      : ""}
                  </h2>
                </label>
                {event?.regions?.length > 1 && (
                  <Select
                    options={event?.regions}
                    value={selectedCurrency}
                    onChange={(newValue) => handleCurrencyChange(newValue)}
                    classNamePrefix="region-select"
                  />
                )}
              </div>
            </div>
          </div>
          <div
            className="event-page__ticket-clear"
            onClick={() => {
              handleClearTickets();
            }}
          >
            {Object.keys(selectedTickets).length !== 0 && (
              <React.Fragment>
                <ClearIcon className="event-page__ticket-clear-icon" />clear
                selection
              </React.Fragment>
            )}
          </div>


          {highLevelCategories
            ?.sort((a, b) => +(a?.position ?? 0) - +(b?.position ?? 0))
            ?.map((category, index) => (
              <React.Fragment key={index}>
                <h3 className="event-page__ticket-category">
                  <span>{category.title}</span>
                </h3>

                {tickets.filter((t) => !t.isSubTier).map((ticket, index) => (
                  <React.Fragment key={index}>
                    {ticket?.category?.pk === category?.pk && (() => {
                      const activeSlot = selectedSlots[ticket.id] || null;
                      const slotSeatLimit = getEffectiveSlotSeatLimit(activeSlot);
                      const selectedTicketCount = selectedTickets[ticket.id]?.count || 0;
                      // One press adds one TABLE for a table tier.
                      const tierIncrement = stepForTier(
                        ticket,
                        parseTierIncrement(ticket.tierIncrement)
                      );
                      const hasReachedSlotLimit =
                        ticket?.hasSlots &&
                        slotSeatLimit != null &&
                        selectedTicketCount + tierIncrement > slotSeatLimit;
                      // The cart counts TABLES, so no table-specific rule is
                      // needed here: one table is one unit of the per-cart
                      // budget, exactly like one ordinary pass.
                      const countLabel = selectedTickets[ticket.id]?.count || 0;
                      const canAddToCart =
                        ticketCount + tierIncrement <= maxTicketsPerCart;
                      const slotAdjustedPrice = getSlotAdjustedPrice(ticket, activeSlot);
                      const isSlotMissing = ticket.hasSlots && !activeSlot;
                      return (
                        <div
                          className={
                            "event-page__ticket-area" +
                            (selectedTickets[ticket.id]
                              ? " event-page__ticket-area--selected"
                              : " event-page__ticket-area--not-selected")
                          }
                          key={index}
                        >
                          <div
                            className="event-page__ticket-row"
                            onClick={() => {
                              if (expandedTickets[ticket.id]) {
                                let old = { ...expandedTickets };
                                delete old[ticket.id];
                                setExpandedTickets(old);
                              } else {
                                setExpandedTickets({
                                  ...expandedTickets,
                                  [ticket.id]: true,
                                });
                              }
                            }}
                            style={{
                              cursor: ticket.description ? "cursor" : "default",
                            }}
                          >
                            <div
                              className={
                                "event-page__ticket-name-wrapper" +
                                (ticket?.state?.type
                                  ? " event-page__ticket-name-wrapper--" +
                                  ticket.state.type
                                  : "")
                              }
                            >
                              <div className="event-page__ticket-header">
                                <p className="event-page__ticket-name">
                                  {ticket.color && (
                                    <span
                                      className="event-page__ticket-color"
                                      style={{ backgroundColor: ticket.color }}
                                    ></span>
                                  )}
                                  {(() => {
                                    const resolvedSubNames = (ticket.subTiers ?? [])
                                      .map((id) => tickets.find((t) => t.id === id)?.name)
                                      .filter(Boolean)
                                      .map((n) => n.replace(/^(Standard|Flexible) Entry:\s*/i, ""));
                                    return resolvedSubNames.length > 0
                                      ? [ticket.name, ...resolvedSubNames].join(" / ")
                                      : ticket.name;
                                  })()}
                                  <span className="event-page__ticket-dropdown-icon-wrapper">
                                    {(ticket.description || ticket.hasSlots) && (
                                      <DropdownIcon
                                        className={
                                          "event-page__ticket-dropdown-icon" +
                                          (expandedTickets[ticket.id]
                                            ? " event-page__ticket-dropdown-icon--expanded"
                                            : "")
                                        }
                                      />
                                    )}
                                  </span>
                                </p>
                              </div>
                            </div>
                            {(ticket.description || ticket.hasSlots) && (
                              <div
                                className={
                                  "event-page__ticket-description" +
                                  (expandedTickets[ticket.id]
                                    ? " event-page__ticket-description--expanded"
                                    : "")
                                }
                                onClick={(e) => {
                                  // Only stopPropagation if the description is expanded
                                  if (!expandedTickets[ticket.id]) e.stopPropagation();
                                }}

                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: ticket.description,
                                  }}
                                ></div>
                                {/*ticket.description*/}
                              </div>
                            )}

                          </div>
                          <div className="event-page__ticket-area-footer" onClick={(e) => e.stopPropagation()}>
                            <div
                              className={
                                "event-page__ticket-price-wrapper" +
                                (ticket?.hasSlots
                                  ? " event-page__ticket-price-wrapper--with-slots"
                                  : "")
                              }
                            >
                              {(ticket?.price !== 0 && !(ticket?.id === 1524 || ticket?.id === 1525)) &&
                                ticket?.state?.type !== "sold-out" && (() => {
                                  const displayPrice = activeSlot?.price != null
                                    ? activeSlot.price / 100
                                    : null;
                                  const showRange = displayPrice == null
                                    && ticket.minPrice != null
                                    && ticket.maxPrice != null
                                    && ticket.minPrice !== ticket.maxPrice;
                                  const isTable = isTableTier(ticket);

                                  return (
                                    <p className="event-page__ticket-price">
                                      {displayPrice != null ? (
                                        <>
                                          {displayPrice.toLocaleString()} {ticket?.currency}
                                        </>
                                      ) : showRange ? (
                                        <>
                                          {ticket.minPrice?.toLocaleString()} – {ticket.maxPrice?.toLocaleString()} {ticket?.currency}
                                        </>
                                      ) : (
                                        <>
                                          {ticket.price?.toLocaleString()} {ticket?.currency}
                                          {isTable && (
                                            <span className="event-page__ticket-seats">
                                              {" "}
                                              / {ticket.tableCapacity} seats
                                            </span>
                                          )}
                                        </>
                                      )}
                                      <p className="event-page__ticket-service-charges">
                                        {ticket?.commissionRate
                                          ? ticket?.hasSlots && displayPrice == null
                                            ? "+ Fees"
                                            : displayPrice != null
                                              ? "+ " +
                                              formatNumbers0(displayPrice * (ticket?.commissionRate / 100)) +
                                              " Fees "
                                              : showRange
                                                ? "+ " +
                                                formatNumbers0(ticket.minPrice * (ticket?.commissionRate / 100)) +
                                                " - " +
                                                formatNumbers0(ticket.maxPrice * (ticket?.commissionRate / 100)) +
                                                " Fees"
                                                : "+ " +
                                                formatNumbers0(ticket.price * (ticket?.commissionRate / 100)) +
                                                " Fees "
                                          : ""}<br />
                                      </p>
                                    </p>
                                  );
                                })()}
                              {(ticket?.id === 1524 || ticket?.id === 1525) && (
                                <>
                                  <div className="ticket-preregistration-trigger">
                                    <button
                                      className="event-page__ticket-prereg-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPreregistrationTicketId(ticket.id);
                                      }}                                  >
                                      {expandedTickets.preregistrationForm
                                        ? "Collapse"
                                        : "Signup For Early Access Now!"}
                                    </button>
                                  </div>

                                  {preregistrationTicketId &&
                                    ReactDOM.createPortal(
                                      (() => {
                                        const preregTicket = tickets.find(
                                          (t) => t.id === preregistrationTicketId
                                        );

                                        if (!preregTicket) return null;

                                        return (
                                          <div
                                            className="overlay ticket-preregistration-overlay"
                                            onClick={() => setPreregistrationTicketId(null)}
                                          >
                                            <div
                                              className="popup ticket-preregistration-popup"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <CloseIcon
                                                className="popup__close"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setPreregistrationTicketId(null);
                                                }}
                                              />
                                              {/* 👇 now this tier is always the one you clicked */}
                                              <PreRegisterForm
                                                key={preregTicket.id}
                                                event={event}
                                                preRegLabel={preregTicket.name3}
                                              />
                                            </div>
                                          </div>
                                        );
                                      })(),
                                      document.body
                                    )}
                                </>
                              )}
                            </div>
                            {/*}
                            <div className="event-page__ticket-state">
                            {ticket?.state?.label}
                            </div>  */}

                            <React.Fragment>
                              {ticket?.state === null && ticket?.hasSlots ? (
                                <div className="event-page__slot-pending">
                                  <button
                                    type="button"
                                    className={
                                      "event-page__slot-select-prompt-link" +
                                      (activeSlot
                                        ? " event-page__slot-select-prompt-link--selected-slot"
                                        : "")
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSlotModal(ticket);
                                    }}
                                  >
                                    {activeSlot
                                      ? formatSelectedSlotDate(activeSlot)
                                      : "Select Entry Slot"}
                                  </button>
                                  {!activeSlot ? (
                                    <div
                                      className="event-page__ticket-counter event-page__ticket-counter--pending-slot"
                                      role="button"
                                      tabIndex={0}
                                      aria-label="Select a slot to choose seats"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openSlotModal(ticket);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openSlotModal(ticket);
                                        }
                                      }}
                                    >
                                      {!event.isSeated && (
                                        <button
                                          type="button"
                                          className="event-page__ticket-counter-icon"
                                          disabled
                                          tabIndex={-1}
                                          aria-hidden="true"
                                        >
                                          <MinusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                      <p
                                        className="event-page__ticket-count event-page__ticket-count--disabled"
                                        aria-hidden="true"
                                      >
                                        {countLabel}
                                      </p>
                                      {!event.isSeated && (
                                        <button
                                          type="button"
                                          className="event-page__ticket-counter-icon"
                                          disabled
                                          tabIndex={-1}
                                          aria-hidden="true"
                                        >
                                          <PlusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="event-page__ticket-counter">
                                      {!event.isSeated && (
                                        <button
                                          className="event-page__ticket-counter-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTicketClick(
                                              ticket.id,
                                              ticket.name,
                                              slotAdjustedPrice,
                                              "subtract",
                                              "",
                                              ticket.currency
                                            );
                                          }}
                                          disabled={
                                            !selectedTickets[ticket?.id] ||
                                            isSlotMissing ||
                                            (!event?.allowMultipleTiers &&
                                              Object?.keys(selectedTickets)
                                                ?.length !== 0 &&
                                              !selectedTickets[ticket?.id])
                                          }
                                        >
                                          <MinusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                      <p
                                        className={`event-page__ticket-count ${!event?.allowMultipleTiers &&
                                          Object?.keys(selectedTickets)?.length !==
                                          0 &&
                                          !selectedTickets[ticket?.id]
                                          ? "event-page__ticket-count--disabled"
                                          : ""
                                          }`}
                                      >
                                        {countLabel}
                                      </p>
                                      {!event?.isSeated && (
                                        <button
                                          className="event-page__ticket-counter-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              (!selectedTickets[ticket.id] ||
                                                canAddToCart) &&
                                              !hasReachedSlotLimit
                                            ) {
                                              handleTicketClick(
                                                ticket.id,
                                                ticket.name,
                                                slotAdjustedPrice,
                                                "add",
                                                "",
                                                ticket.currency
                                              );
                                            }
                                          }}
                                          disabled={
                                            !canAddToCart ||
                                            isSlotMissing ||
                                            hasReachedSlotLimit ||
                                            (!event?.allowMultipleTiers &&
                                              Object?.keys(selectedTickets)
                                                ?.length !== 0 &&
                                              !selectedTickets[ticket?.id])
                                          }
                                        >
                                          <PlusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={
                                    "event-page__ticket-counter" +
                                    (ticket?.state?.type
                                      ? " event-page__ticket-counter--with-state"
                                      : "")
                                  }
                                >
                                  {ticket?.state === null ? (
                                    <React.Fragment>
                                      {!event.isSeated && (
                                        <button
                                          className="event-page__ticket-counter-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTicketClick(
                                              ticket.id,
                                              ticket.name,
                                              slotAdjustedPrice,
                                              "subtract",
                                              "",
                                              ticket.currency
                                            );
                                          }}
                                          disabled={
                                            !selectedTickets[ticket?.id] ||
                                            isSlotMissing ||
                                            (!event?.allowMultipleTiers &&
                                              Object?.keys(selectedTickets)
                                                ?.length !== 0 &&
                                              !selectedTickets[ticket?.id])
                                          }
                                        >
                                          <MinusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                      <p
                                        className={`event-page__ticket-count ${!event?.allowMultipleTiers &&
                                          Object?.keys(selectedTickets)?.length !==
                                          0 &&
                                          !selectedTickets[ticket?.id]
                                          ? "event-page__ticket-count--disabled"
                                          : ""
                                          }`}
                                      >
                                        {countLabel}
                                      </p>
                                      {!event?.isSeated && (
                                        <button
                                          className="event-page__ticket-counter-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              (!selectedTickets[ticket.id] ||
                                                canAddToCart) &&
                                              !hasReachedSlotLimit
                                            ) {
                                              handleTicketClick(
                                                ticket.id,
                                                ticket.name,
                                                slotAdjustedPrice,
                                                "add",
                                                "",
                                                ticket.currency
                                              );
                                            }
                                          }}
                                          disabled={
                                            !canAddToCart ||
                                            isSlotMissing ||
                                            hasReachedSlotLimit ||
                                            (!event?.allowMultipleTiers &&
                                              Object?.keys(selectedTickets)
                                                ?.length !== 0 &&
                                              !selectedTickets[ticket?.id])
                                          }
                                        >
                                          <PlusBlack className="event-page__ticket-counter-icon" />
                                        </button>
                                      )}
                                    </React.Fragment>
                                  ) : (
                                    <p className="event-page__ticket-state">
                                      {ticket?.state?.label}
                                    </p>
                                  )}
                                  {/*
                              <div className="event-page__ticket-limit">
                                  Special Message
                              </div>
                              */}
                                </div>
                              )}
                            </React.Fragment>
                          </div>
                        </div>
                      );
                    })()}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
        </div>
      </div>
      {slotModalTicketId &&
        ReactDOM.createPortal(
          (() => {
            const modalTicket = tickets.find((ticket) => ticket.id === slotModalTicketId);
            if (!modalTicket) return null;

            const slotTicketsGroup = buildSlotTicketsWithSubs(modalTicket, tickets);
            const useCombinedSubTierSlots = slotTicketsGroup.length >= 2;

            return (
              <div className="overlay event-page__slot-modal-overlay" onClick={closeSlotModal}>
                <div
                  className="popup event-page__slot-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CloseIcon
                    className="popup__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeSlotModal();
                    }}
                  />
                  <div className="event-page__slot-modal-body">
                    <h4 className="event-page__slot-modal-title">
                      {useCombinedSubTierSlots
                        ? `Select slot for ${modalTicket.name.replace(/^(Standard|Flexible) Entry:\s*/i, "")}`
                        : `Select slot for ${modalTicket.name}`}
                    </h4>
                    <SlotSelector
                      key={`slot-${modalTicket.id}-${useCombinedSubTierSlots ? "multi" : "single"}`}
                      eventPk={event?.id}
                      tierPk={modalTicket.id}
                      tier={modalTicket}
                      tierSlotGroup={useCombinedSubTierSlots ? slotTicketsGroup : undefined}
                      eventStartDate={event?.startsAtRaw}
                      maxDaysToShow={
                        modalTicket?.maxDaysToShow ?? modalTicket?.max_days_to_show
                      }
                      selectedSlot={pendingSlotSelection}
                      currency={modalTicket.currency}
                      onSlotSelect={(slot) => {
                        const bookingTierId = slot._bookingTierId ?? modalTicket.id;
                        const slotWithBookingTier = {
                          ...slot,
                          _bookingTierId: bookingTierId,
                        };
                        setPendingSlotSelection(slotWithBookingTier);
                        const tierIncrement = stepForTier(
                        modalTicket,
                        parseTierIncrement(modalTicket.tierIncrement)
                      );
                        const currentCount = selectedTickets[modalTicket.id]?.count || 0;
                        setPendingPersonCount(currentCount >= tierIncrement ? currentCount : tierIncrement);
                        setTimeout(() => {
                          personsCounterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 80);
                      }}
                    />

                    {pendingSlotSelection && (() => {
                      const tierIncrement = parseTierIncrement(modalTicket.tierIncrement);
                      const otherTierCount = Object.entries(selectedTickets)
                        .filter(([k]) => String(k) !== String(modalTicket.id))
                        .reduce((sum, [, v]) => sum + (v?.count ?? 0), 0);
                      const remainingCapacity = maxTicketsPerCart - otherTierCount;
                      const slotSeatLimit = getEffectiveSlotSeatLimit(pendingSlotSelection);
                      const maxCount = slotSeatLimit != null
                        ? Math.min(remainingCapacity, slotSeatLimit)
                        : remainingCapacity;
                      const canConfirm = pendingPersonCount >= tierIncrement && pendingPersonCount <= maxCount;
                      const slotSummary = formatSlotSelectionSummary(pendingSlotSelection);
                      const capacityHint =
                        maxCount <= 1
                          ? "1 ticket available for this slot"
                          : `Up to ${maxCount} tickets for this slot`;

                      return (
                        <div ref={personsCounterRef} className="event-page__slot-persons-step">
                          <div className="event-page__slot-persons-summary">
                            <span className="event-page__slot-persons-summary-badge">Selected</span>
                            <span className="event-page__slot-persons-summary-value">
                              {slotSummary}
                            </span>
                          </div>

                          <div className="event-page__slot-persons-card">
                            <div className="event-page__slot-persons-header">
                              <div className="event-page__slot-persons-copy">
                                <p className="event-page__slot-persons-label">
                                  How many tickets?
                                </p>
                                <p className="event-page__slot-persons-hint">
                                  {capacityHint}
                                </p>
                              </div>
                              <div className="event-page__ticket-counter event-page__slot-persons-counter">
                                <button
                                  type="button"
                                  className="event-page__ticket-counter-icon"
                                  aria-label="Decrease ticket count"
                                  onClick={() =>
                                    setPendingPersonCount((prev) =>
                                      Math.max(tierIncrement, prev - tierIncrement)
                                    )
                                  }
                                  disabled={pendingPersonCount <= tierIncrement}
                                >
                                  <MinusBlack className="event-page__ticket-counter-icon" />
                                </button>
                                <p className="event-page__ticket-count">{pendingPersonCount}</p>
                                <button
                                  type="button"
                                  className="event-page__ticket-counter-icon"
                                  aria-label="Increase ticket count"
                                  onClick={() =>
                                    setPendingPersonCount((prev) =>
                                      Math.min(maxCount, prev + tierIncrement)
                                    )
                                  }
                                  disabled={pendingPersonCount >= maxCount}
                                >
                                  <PlusBlack className="event-page__ticket-counter-icon" />
                                </button>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="event-page__slot-persons-confirm-btn"
                              disabled={!canConfirm}
                              onClick={() => {
                                applySlotSelectionWithCount(
                                  modalTicket.id,
                                  pendingSlotSelection,
                                  pendingPersonCount
                                );
                                closeSlotModal();
                                onSlotConfirmed();
                              }}
                            >
                              Confirm {pendingPersonCount}{" "}
                              {pendingPersonCount === 1 ? "ticket" : "tickets"}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })(),
          document.body
        )}
      {loading && (
        <CTAPopup
          actionHandler={null}
          variation="no-action"
          displayMessage="We are fetching your tickets, this could take a few moments.."
          showOverlay={true}
          loading={loading}
        />
      )}
      {!loading && !fetchError && (
        <CTAPopup
          actionHandler={() => {
            if (btnState.state === "nochange") {
              nextStep();
            } else {
              handleTicketsFetch();
            }
          }}
          title=""
          variation="single"
          text="Checkout"
          /* text2={ticketCount === 0 ? "" : btnState.label} */
          disabled={Object.keys(selectedTickets).length === 0}
          expiry={expiry}
        />
      )}
      {!loading && fetchError && (
        <CTAPopup
          actionHandler={handleRetry}
          warning="OOPS!"
          displayMessage={fetchError}
          variation="error"
          text="Cancel"
          text2="Retry"
          cancel={() => setFetchError("")}
          showOverlay={true}
        />
      )}
    </React.Fragment>
  );
};

export default TicketingDetails;
